import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { stopSubmit, touch, change } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { FORM_FIELDS, FORM_NAME, REQUEST_ERROR_MESSAGE } from './constants';
import { transformToValidationErrors } from './utils';
import { DISMISS_TIME } from '../../api/constants';
import {
	CREATE_TRAILER,
	DELETE_TRAILER,
	GET_TRAILER_INFO,
	GET_LEGALS,
	GET_TRAILER_TYPES,
	GET_MODES,
	GET_OWNERSHIPS,
	setTrailerInfoAction,
	setLegalsAction,
	setIsLegalsLoadingAction,
	setTrailerTypesAction,
	setIsTrailerDeletingAction,
	setIsTrailerInfoLoadingAction,
	setIsTrailerSavingAction,
	setIsTrailerTypesLoadingAction,
	setIsModesLoadingAction,
	setIsOwnershipsLoadingAction,
	setModesAction,
	setOwnershipsAction,
	UPDATE_TRAILER,
	setIsEventDataLoadingAction,
	setEventDataAction,
	GET_EVENT_DATA,
} from './actions';
import {
	saveNewTrailerRequest,
	deleteTrailerRequest,
	getTrailerInfoRequest,
	getLegalsDictionaryRequest,
	getTrailerTypesRequest,
	getModesDictionaryRequest,
	getOwnershipsDictionaryRequest,
	updateTrailerRequest,
	getTrailerEventRequest,
} from '../../api/requests';
import { getDataForCreateOrUpdateTrailerSelector, getFormIsInvalidSelector } from './selectors';
import { getTrailersListSelector, getRawPaginationSelector } from '../trailers/selectors';
import { setTrailersListAction, setPaginationAction, getTrailersListAction } from '../trailers/actions';
import { errorHandler } from '../../api/utils';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { getTransportsListAction } from '../transports/actions';

export function* getTrailerInfoSaga({ id }) {
	try {
		yield put(setIsTrailerInfoLoadingAction(true));

		const { data, message, toast } = yield call(getTrailerInfoRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setTrailerInfoAction(data));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTrailerInfoLoadingAction(false));
	}
}

export function* deleteTrailerSaga({ payload: { id, redirect, query } }) {
	try {
		yield put(setIsTrailerDeletingAction(true));

		const { data, message, toast } = yield call(deleteTrailerRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const trailers = yield select(getTrailersListSelector());
			yield put(setTrailersListAction(trailers.filter(({ id }) => id !== data.id)));

			const pagination = yield select(getRawPaginationSelector());
			if (pagination) {
				yield put(setPaginationAction({ ...pagination, total: pagination.total - 1 }));
			}

			redirect();
			yield put(getTrailersListAction({ searchQuery: removeModalQuery(query.toString()), noLoading: true }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTrailerDeletingAction(false));
	}
}

export function* createTrailerSaga({ payload: { redirect, resetState, searchQuery, returnData } }) {
	try {
		yield put(setIsTrailerSavingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForCreateOrUpdateTrailerSelector());

		const { data, errors, message, toast } = yield call(saveNewTrailerRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			yield put(setTrailerInfoAction(data));

			const trailers = yield select(getTrailersListSelector());
			yield put(
				setTrailersListAction([
					{
						...data,
						type_name: data.type?.name ?? '---',
						link: `?${searchQuery ? `${searchQuery}&` : ''}modal=trailer&id=${data.id}`,
					},
					...trailers,
				]),
			);

			const pagination = yield select(getRawPaginationSelector());

			if (pagination) {
				yield put(setPaginationAction({ ...pagination, total: pagination.total + 1 }));
			}

			// Если прицеп добавляется из карточки машины, то нужно вернуться назад на карточку машины
			if (returnData) {
				yield put(change('cars-form', 'trailer', data.id));
				returnData.history.push(`${returnData.pathname}?${returnData.query.toString()}`);
			} else {
				redirect();
			}
			yield put(
				getTrailersListAction({ searchQuery: removeModalQuery(searchQuery.toString()), noLoading: true }),
			);

			resetState();
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTrailerSavingAction(false));
	}
}

export function* updateTrailerSaga({ payload: { id, redirect, searchQuery, pathname } }) {
	try {
		yield put(setIsTrailerSavingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForCreateOrUpdateTrailerSelector());

		const { data, errors, message, toast } = yield call(updateTrailerRequest, id, dataForSave);
		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			const trailers = yield select(getTrailersListSelector());

			const updatedTrailer = {
				...data,
				type_name: data.type?.name ?? '---',
				link: `?${searchQuery ? `${searchQuery}&` : ''}modal=trailer&id=${data.id}`,
			};

			yield put(
				setTrailersListAction(
					trailers.map(trailer => {
						return trailer.id === data.id ? updatedTrailer : trailer;
					}),
				),
			);

			yield put(setTrailerInfoAction(data));

			redirect();

			if (pathname.includes('quality_block')) {
				const type = pathname.split('/')[2];
				yield put(
					getTransportsListAction({
						type,
						searchQuery: removeModalQuery(searchQuery.toString()),
						noLoading: true,
					}),
				);
			} else {
				yield put(
					getTrailersListAction({ searchQuery: removeModalQuery(searchQuery.toString()), noLoading: true }),
				);
			}
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...FORM_FIELDS));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTrailerSavingAction(false));
	}
}

export function* getOwnershipsSaga() {
	try {
		yield put(setIsOwnershipsLoadingAction(true));

		const { data, message, toast } = yield call(getOwnershipsDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setOwnershipsAction([
					...data.map(ownership => ({
						key: ownership.id,
						value: ownership.id,
						text: ownership.name,
					})),
				]),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOwnershipsLoadingAction(false));
	}
}

export function* getLegalsSaga() {
	try {
		yield put(setIsLegalsLoadingAction(true));

		const { data, message, toast } = yield call(getLegalsDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setLegalsAction(
					data.map(legal => ({
						key: legal.id,
						value: legal.id,
						text: legal.name,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsLegalsLoadingAction(false));
	}
}

export function* getTypesSaga() {
	try {
		yield put(setIsTrailerTypesLoadingAction(true));

		const { data, message, toast } = yield call(getTrailerTypesRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setTrailerTypesAction(
					data.map(type => ({ key: String(type.id), value: String(type.id), text: type.name })),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsTrailerTypesLoadingAction(false));
	}
}

export function* getModesDataSaga() {
	try {
		yield put(setIsModesLoadingAction(true));

		const { data, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setModesAction(
					data
						.filter(mode => mode.id !== 1)
						.map(mode => ({ key: String(mode.id), value: String(mode.id), text: mode.name_with_symbol })),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesLoadingAction(false));
	}
}

export function* getEventDataSaga({ payload: id }) {
	try {
		yield put(setIsEventDataLoadingAction(true));

		const { message, toast, reports } = yield call(getTrailerEventRequest, id);

		if (reports) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(setEventDataAction(reports?.html || null));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsEventDataLoadingAction(false));
	}
}

export default function* trailersModalSaga() {
	yield takeEvery(GET_TRAILER_INFO, getTrailerInfoSaga);
	yield takeEvery(DELETE_TRAILER, deleteTrailerSaga);
	yield takeEvery(CREATE_TRAILER, createTrailerSaga);
	yield takeEvery(UPDATE_TRAILER, updateTrailerSaga);
	yield takeEvery(GET_OWNERSHIPS, getOwnershipsSaga);
	yield takeEvery(GET_LEGALS, getLegalsSaga);
	yield takeEvery(GET_TRAILER_TYPES, getTypesSaga);
	yield takeEvery(GET_MODES, getModesDataSaga);
	yield takeEvery(GET_EVENT_DATA, getEventDataSaga);
}
